import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { Drawer, useToast } from "@/components/system";
import {
  Row,
  FormInput,
  FormSelect,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  localNowDatetime,
  browserTz,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * Quick-log weight (SPEC 3.1.4). Writes one row to `body_measurement` with
 * sensor_source='manual'. Required: weight_kg, ts. Optional: body_fat_pct,
 * time_of_day.
 *
 * Recompute of AthleteProfile.current_weight_kg / current_body_fat_pct is
 * deferred to a Build Week 5 trigger function (SPEC 1.2.4 cross-cutting:
 * "Recompute current_* fields when underlying entries change").
 */

const TIME_OPTIONS = [
  { value: "morning_pre_food", label: "Morning (pre food)" },
  { value: "morning_post_food", label: "Morning (post food)" },
  { value: "midday", label: "Midday" },
  { value: "evening", label: "Evening" },
  { value: "other", label: "Other" },
];

export function LogWeightDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const [weight, setWeight] = useState("");
  const [bodyFatPct, setBodyFatPct] = useState("");
  const [timeOfDay, setTimeOfDay] = useState("morning_pre_food");
  const [ts, setTs] = useState(localNowDatetime());

  const mutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      if (!weight) throw new Error("Weight is required");
      const { data, error } = await supabase
        .from("body_measurement")
        .insert({
          user_id: userId,
          ts: new Date(ts).toISOString(),
          tz: browserTz(),
          weight_kg: Number(weight),
          body_fat_pct: bodyFatPct ? Number(bodyFatPct) : null,
          time_of_day: timeOfDay,
          sensor_source: "manual",
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["body_measurements", userId] });
      void qc.invalidateQueries({ queryKey: ["athlete_profile", userId] });
      toast.show(`Weight ${weight} kg logged.`);
      reset();
      onClose();
    },
    onError: (e) => toast.show(`Couldn't log weight: ${formatError(e)}`),
  });

  function reset() {
    setWeight("");
    setBodyFatPct("");
    setTimeOfDay("morning_pre_food");
    setTs(localNowDatetime());
  }

  return (
    <Drawer
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Log weight"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
      >
        <Row>
          <FormInput
            label="Weight (kg)"
            inputMode="decimal"
            value={weight}
            onChange={setWeight}
            placeholder="64.5"
            required
            autoFocus
          />
          <FormInput
            label="Body fat (%)"
            inputMode="decimal"
            value={bodyFatPct}
            onChange={setBodyFatPct}
            placeholder="14.2"
          />
        </Row>
        <Row>
          <FormSelect
            label="Time of day"
            value={timeOfDay}
            onChange={setTimeOfDay}
            options={TIME_OPTIONS}
          />
          <FormInput
            label="When"
            type="datetime-local"
            value={ts}
            onChange={setTs}
          />
        </Row>

        <DialogFooter>
          <SecondaryButton
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={mutation.isPending}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={mutation.isPending || !weight}>
            {mutation.isPending ? "Saving…" : "Log weight"}
          </PrimaryButton>
        </DialogFooter>
      </form>
    </Drawer>
  );
}
